/**
 * The statuses this screen can write into dbo.StatusHistory.
 *
 * A history row stores a single StatusId — never the (StatusId, InternationalStatusId)
 * pair that dbo.Orders carries. For an international order the row records the
 * SUB-status, so `14` is never written: an international delivery is `6`, exactly as
 * InternationalAdminChangeOrderStatus_sp and the AMK bulk operations do it.
 */
export interface HistoryStatusOption {
	key: string;
	/** Written to StatusHistory.StatusId. Same value local or international. */
	statusId: number;
	/** The pair on dbo.Orders this corresponds to, shown for context only. */
	internationalPair: string;
	labelKey: string;
	fallbackLabel: string;
	/** Default description, before the "(FirstName LastName)" suffix. */
	defaultComment: string;
	/**
	 * "Duke u kthyer" exists only as the international pair (14,10) in
	 * OrderRepository's label table — there is no local equivalent — so it is offered
	 * only for international orders.
	 */
	internationalOnly?: boolean;
	/**
	 * The mirror case: "Fletë Kthim" and "Kthim Fletë Kthim" exist as (18,null) and
	 * (19,null) — plain local pairs — so they are offered only for local orders.
	 */
	localOnly?: boolean;
}

export const HISTORY_STATUSES: HistoryStatusOption[] = [
	{
		key: "inTransport",
		statusId: 5,
		internationalPair: "(14, 5)",
		labelKey: "statusInTransport",
		fallbackLabel: "Në transport",
		defaultComment: "Porosia eshte vendosur ne transport",
	},
	{
		key: "waiting",
		statusId: 8,
		internationalPair: "(14, 8)",
		labelKey: "statusWaiting",
		fallbackLabel: "Në pritje",
		defaultComment: "Porosia eshte vendosur ne pritje",
	},
	{
		key: "refused",
		statusId: 7,
		internationalPair: "(14, 7)",
		labelKey: "statusRefused",
		fallbackLabel: "Refuzuar",
		defaultComment: "Porosia eshte refuzuar",
	},
	{
		key: "delivered",
		statusId: 6,
		internationalPair: "(14, 6)",
		labelKey: "statusDelivered",
		fallbackLabel: "Dorëzuar",
		defaultComment: "Porosia eshte dorezuar",
	},
	{
		key: "returning",
		statusId: 10,
		internationalPair: "(14, 10)",
		labelKey: "statusReturning",
		fallbackLabel: "Duke u kthyer",
		defaultComment: "Porosia eshte duke u kthyer",
		internationalOnly: true,
	},
	{
		// Same StatusId as "Duke u kthyer" — on an international order only the comment
		// tells them apart, which is how the existing code behaves too.
		key: "returned",
		statusId: 10,
		internationalPair: "(10, 14)",
		labelKey: "statusReturned",
		fallbackLabel: "Kthyer",
		defaultComment: "Porosia eshte kthyer",
	},
	{
		key: "returnSheet",
		statusId: 18,
		internationalPair: "(18, null)",
		labelKey: "statusReturnSheet",
		fallbackLabel: "Fletë Kthim",
		defaultComment: "Porosia eshte vendosur ne flete kthim",
		localOnly: true,
	},
	{
		key: "returnSheetReturn",
		statusId: 19,
		internationalPair: "(19, null)",
		labelKey: "statusReturnSheetReturn",
		fallbackLabel: "Kthim Fletë Kthim",
		defaultComment: "Porosia eshte kthyer nga flete kthimi",
		localOnly: true,
	},
];

export const getAvailableStatuses = (isInternational: boolean) =>
	HISTORY_STATUSES.filter((s) =>
		isInternational ? !s.localOnly : !s.internationalOnly
	);

/** Fallback label for a history row whose StatusId the API could not name. */
export const localStatusLabel = (statusId: number): string | undefined =>
	HISTORY_STATUSES.find(
		(s) => s.statusId === statusId && !s.internationalOnly
	)?.fallbackLabel;
